<template>
  <div class="auto-system-classification">
    <div class="card mb-25 border-0 rounded-0 bg-white add-product-box">
      <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
        <div v-if="apiError" class="alert alert-danger" role="alert">
          {{ apiError }}
        </div>

        <form @submit.prevent="submitForm" novalidate>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">Order Data Table <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control shadow-none rounded-0 text-black mb-2"
                  placeholder="Search order tables"
                  v-model.trim="orderTableSearch"
                />
                <select
                  class="form-control shadow-none rounded-0 text-black"
                  v-model="form.orderDataTableName"
                  @blur="markTouched('orderDataTableName')"
                >
                  <option disabled value="">Select order data table</option>
                  <option
                    v-for="table in filteredOrderTables"
                    :key="table.tableID"
                    :value="table.tableID"
                  >
                    {{ table.name || table.tableID }}
                  </option>
                </select>
                <small v-if="showErrors && errors.orderDataTableName" class="text-danger">{{ errors.orderDataTableName }}</small>
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">SKU Data Table <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control shadow-none rounded-0 text-black mb-2"
                  placeholder="Search SKU tables"
                  v-model.trim="skuTableSearch"
                />
                <select
                  class="form-control shadow-none rounded-0 text-black"
                  v-model="form.skuDataTableName"
                  @blur="markTouched('skuDataTableName')"
                >
                  <option disabled value="">Select SKU data table</option>
                  <option
                    v-for="table in filteredSkuTables"
                    :key="table.tableID"
                    :value="table.tableID"
                  >
                    {{ table.name || table.tableID }}
                  </option>
                </select>
                <small v-if="showErrors && errors.skuDataTableName" class="text-danger">{{ errors.skuDataTableName }}</small>
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">Analysis Start Date</label>
                <input
                  type="datetime-local"
                  class="form-control shadow-none rounded-0 text-black"
                  v-model="form.analysisStartDate"
                  @blur="markTouched('analysisStartDate')"
                />
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">Analysis End Date</label>
                <input
                  type="datetime-local"
                  class="form-control shadow-none rounded-0 text-black"
                  v-model="form.analysisEndDate"
                  @blur="markTouched('analysisEndDate')"
                />
              </div>
            </div>

            <div class="col-md-12" v-if="showErrors && errors.dates">
              <small class="text-danger">{{ errors.dates }}</small>
            </div>
          </div>

          <div class="advanced-wrapper mt-3">
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="advancedOpen = !advancedOpen"
              :aria-expanded="advancedOpen.toString()"
            >
              {{ advancedOpen ? 'Hide Advanced Options' : 'Show Advanced Options' }}
            </button>

            <div v-show="advancedOpen" class="advanced-content mt-3">
              <div class="row">
                <div class="col-12">
                  <label class="d-block text-black fw-semibold mb-10">Filters</label>
                  <div v-if="filters.length === 0" class="text-muted small mb-2">No filters configured.</div>
                  <div
                    v-for="(filter, index) in filters"
                    :key="filter.id"
                    class="row g-2 align-items-start mb-2"
                  >
                    <div class="col-md-5">
                      <input
                        type="text"
                        class="form-control shadow-none rounded-0 text-black"
                        placeholder="Column name"
                        v-model="filter.columnName"
                      />
                    </div>
                    <div class="col-md-5">
                      <input
                        type="text"
                        class="form-control shadow-none rounded-0 text-black"
                        placeholder="Comma separated values"
                        v-model="filter.values"
                      />
                    </div>
                    <div class="col-md-2 d-flex">
                      <button type="button" class="btn btn-outline-danger w-100" @click="removeFilter(filter.id)">Remove</button>
                    </div>
                    <div class="col-12" v-if="showErrors && errors.filters[index]">
                      <small class="text-danger">{{ errors.filters[index] }}</small>
                    </div>
                  </div>
                  <button type="button" class="btn btn-outline-primary" @click="addFilter">Add Filter</button>
                </div>

                <div class="col-md-6 mt-4 mt-md-5">
                  <div class="form-check form-switch">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="ensure-indices"
                      v-model="form.ensureIndices"
                    />
                    <label class="form-check-label" for="ensure-indices">Ensure indices before running (recommended)</label>
                  </div>
                </div>

                <div class="col-md-6 mt-4 mt-md-5">
                  <div class="form-group">
                    <label class="d-block text-black fw-semibold mb-10">Working Table Override</label>
                    <input
                      type="text"
                      class="form-control shadow-none rounded-0 text-black"
                      placeholder="Optional override"
                      v-model="form.workingTableOverride"
                    />
                  </div>
                </div>

                <div class="col-12 mt-4">
                  <label class="d-block text-black fw-semibold mb-10">Outbound Order Containers</label>
                  <div v-if="containers.length === 0" class="text-muted small mb-2">No containers configured.</div>
                  <div
                    v-for="(container, index) in containers"
                    :key="container.id"
                    class="row g-2 align-items-start mb-2"
                  >
                    <div class="col-xl-3 col-md-6">
                      <input
                        type="text"
                        class="form-control shadow-none rounded-0 text-black"
                        placeholder="Name"
                        v-model="container.name"
                      />
                    </div>
                    <div class="col-xl-3 col-md-6">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        class="form-control shadow-none rounded-0 text-black"
                        placeholder="Width"
                        v-model.number="container.width"
                      />
                    </div>
                    <div class="col-xl-3 col-md-6">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        class="form-control shadow-none rounded-0 text-black"
                        placeholder="Height"
                        v-model.number="container.height"
                      />
                    </div>
                    <div class="col-xl-3 col-md-6">
                      <div class="d-flex gap-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          class="form-control shadow-none rounded-0 text-black"
                          placeholder="Depth"
                          v-model.number="container.depth"
                        />
                        <button type="button" class="btn btn-outline-danger" @click="removeContainer(container.id)">Remove</button>
                      </div>
                    </div>
                    <div class="col-12" v-if="showErrors && errors.containers[index]">
                      <small class="text-danger">{{ errors.containers[index] }}</small>
                    </div>
                  </div>
                  <button type="button" class="btn btn-outline-primary" @click="addContainer">Add Container</button>
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex flex-wrap gap-3 mt-4">
            <button
              class="default-btn transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 rounded-1 fs-md-15 fs-lg-16"
              type="submit"
              :disabled="isSubmitting || !isFormValid"
            >
              <span v-if="!isSubmitting">Run Classification</span>
              <span v-else class="d-inline-flex align-items-center gap-2">
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Running...
              </span>
            </button>
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="resetForm"
              :disabled="isSubmitting"
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-6">
        <div class="card mb-25 border-0 rounded-0 bg-white">
          <div class="card-header bg-white p-3">
            <h6 class="mb-0">Request JSON Preview</h6>
          </div>
          <div class="card-body p-3">
            <pre class="preview-block">{{ requestJsonPreview }}</pre>
          </div>
        </div>
      </div>
      <div class="col-lg-6">
        <div class="card mb-25 border-0 rounded-0 bg-white">
          <div class="card-header bg-white p-3">
            <h6 class="mb-0">Containers JSON Preview</h6>
          </div>
          <div class="card-body p-3">
            <pre class="preview-block">{{ containersJsonPreview }}</pre>
          </div>
        </div>
      </div>
    </div>

    <div v-if="result" class="card mb-25 border-0 rounded-0 bg-white">
      <div class="card-header bg-white p-3">
        <h6 class="mb-0">Classification Results</h6>
      </div>
      <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
        <div class="row mb-3">
          <div class="col-md-4">
            <strong>Working Table:</strong>
            <div>{{ result?.workingTable ?? 'Not provided' }}</div>
          </div>
          <div class="col-md-4">
            <strong>Window:</strong>
            <div>{{ windowLabel }}</div>
          </div>
          <div class="col-md-4">
            <strong>Status:</strong>
            <div>{{ result?.status ?? 'Unknown' }}</div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-4">
            <strong>Total Lines</strong>
            <div>{{ headline.totalLines ?? '�' }}</div>
          </div>
          <div class="col-md-4">
            <strong>Total Volume (m�)</strong>
            <div>{{ headline.totalVolume_m3 ?? '�' }}</div>
          </div>
          <div class="col-md-4">
            <strong>Unique SKUs</strong>
            <div>{{ headline.uniqueSkus ?? '�' }}</div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-4">
            <h6>By Category</h6>
            <pre class="preview-block">{{ formatCompactJson(result?.summary?.byCategory) }}</pre>
          </div>
          <div class="col-md-4">
            <h6>Per-SKU Tote Ranking</h6>
            <pre class="preview-block">{{ formatCompactJson(result?.summary?.perSkuToteRanking) }}</pre>
          </div>
          <div class="col-md-4">
            <h6>Per-SKU Daily Buckets</h6>
            <pre class="preview-block">{{ formatCompactJson(result?.summary?.perSkuDailyBuckets) }}</pre>
          </div>
        </div>

        <div class="mt-3">
          <h6>Materialised Tables</h6>
          <ul class="mb-0" v-if="materialisedTables.length">
            <li v-for="table in materialisedTables" :key="table">{{ table }}</li>
          </ul>
          <div v-else class="text-muted small">No materialised tables reported.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, computed, watch, onMounted } from 'vue';
import axios from 'axios';
import type { AxiosError } from 'axios';
import { BASE_HSUITE_URL } from '@/core/constant/env';

type DataTableOption = {
  tableID: string;
  name?: string;
  [key: string]: unknown;
};

interface ClassificationWindow {
  start?: string | null;
  end?: string | null;
}

interface ClassificationHeadline {
  totalLines?: number | null;
  totalVolume_m3?: number | null;
  uniqueSkus?: number | null;
}

interface AutoSystemClassificationSummary {
  window?: ClassificationWindow | null;
  headline?: ClassificationHeadline | null;
  byCategory?: unknown;
  perSkuToteRanking?: unknown;
  perSkuDailyBuckets?: unknown;
}

interface AutoSystemClassificationResponse {
  status?: string;
  workingTable?: string;
  summary?: AutoSystemClassificationSummary | null;
  materialised?: string[];
  [key: string]: unknown;
}
type FilterRow = {
  id: number;
  columnName: string;
  values: string;
};

type ContainerRow = {
  id: number;
  name: string;
  width: number | null;
  height: number | null;
  depth: number | null;
};

export default defineComponent({
  name: 'CreateAutoSystemClassification',
  props: {
    dataStreamID: {
      type: Number,
      required: true
    },
    leadID: {
      type: Number,
      required: true
    }
  },
  setup(props) {
    const form = reactive({
      orderDataTableName: '',
      skuDataTableName: '',
      analysisStartDate: '',
      analysisEndDate: '',
      ensureIndices: true,
      workingTableOverride: ''
    });

    const filters = ref<FilterRow[]>([]);
    const containers = ref<ContainerRow[]>([]);
    const orderTables = ref<DataTableOption[]>([]);
    const skuTables = ref<DataTableOption[]>([]);
    const orderTableSearch = ref('');
    const skuTableSearch = ref('');

    const isSubmitting = ref(false);
    const submitAttempted = ref(false);
    const touchedFields = reactive<Record<string, boolean>>({});
    const advancedOpen = ref(false);
    const apiError = ref<string | null>(null);
    const result = ref<AutoSystemClassificationResponse | null>(null);
    const materialisedTables = ref<string[]>([]);

    const generateRowId = () => Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);

    const addFilter = () => {
      filters.value.push({
        id: generateRowId(),
        columnName: '',
        values: ''
      });
    };

    const removeFilter = (id: number) => {
      filters.value = filters.value.filter((filter) => filter.id !== id);
    };

    const addContainer = () => {
      containers.value.push({
        id: generateRowId(),
        name: '',
        width: null,
        height: null,
        depth: null
      });
    };

    const removeContainer = (id: number) => {
      containers.value = containers.value.filter((container) => container.id !== id);
    };

    const markTouched = (field: string) => {
      touchedFields[field] = true;
    };

    const fetchOrderTables = async () => {
      if (!props.dataStreamID) {
        return;
      }
      try {
        const response = await axios.get(`${BASE_HSUITE_URL}/api/DataTable/GetFilteredDataTables`, {
          params: {
            LeadID: props.dataStreamID,
            tableID: 'OrderData'
          }
        });
        if (response.data?.dataTables) {
          orderTables.value = response.data.dataTables;
        }
      } catch (error) {
        console.error('Error fetching order tables', error);
      }
    };

    const fetchSkuTables = async () => {
      if (!props.dataStreamID) {
        return;
      }
      try {
        const response = await axios.get(`${BASE_HSUITE_URL}/api/DataTable/GetFilteredDataTables`, {
          params: {
            LeadID: props.dataStreamID,
            tableID: 'SKUData'
          }
        });
        if (response.data?.dataTables) {
          skuTables.value = response.data.dataTables;
        }
      } catch (error) {
        console.error('Error fetching SKU tables', error);
      }
    };

    const filteredOrderTables = computed(() => {
      const term = orderTableSearch.value.trim().toLowerCase();
      if (!term) {
        return orderTables.value;
      }
      return orderTables.value.filter((table) => {
        const name = (table.name ?? '').toLowerCase();
        const tableId = (table.tableID ?? '').toLowerCase();
        return name.includes(term) || tableId.includes(term);
      });
    });

    const filteredSkuTables = computed(() => {
      const term = skuTableSearch.value.trim().toLowerCase();
      if (!term) {
        return skuTables.value;
      }
      return skuTables.value.filter((table) => {
        const name = (table.name ?? '').toLowerCase();
        const tableId = (table.tableID ?? '').toLowerCase();
        return name.includes(term) || tableId.includes(term);
      });
    });

    const buildFiltersPayload = () => {
      const payload: Record<string, string[]> = {};
      filters.value.forEach((filter) => {
        const column = filter.columnName.trim();
        if (!column) {
          return;
        }
        const values = filter.values
          .split(',')
          .map((value) => value.trim())
          .filter((value) => value.length > 0);
        if (values.length > 0) {
          payload[column] = values;
        }
      });
      return payload;
    };

    const buildContainersPayload = () => {
      return containers.value
        .map((container) => {
          if (!container) {
            return null;
          }
          const name = container.name.trim();
          const width = container.width;
          const height = container.height;
          const depth = container.depth;
          const populated = name || width !== null || height !== null || depth !== null;
          if (!populated) {
            return null;
          }
          return {
            Name: name,
            Width: width ?? 0,
            Height: height ?? 0,
            Depth: depth ?? 0
          };
        })
        .filter((entry): entry is { Name: string; Width: number; Height: number; Depth: number } => entry !== null);
    };

    const errors = computed(() => {
      const filterErrors = filters.value.map((filter) => {
        const hasColumn = filter.columnName.trim().length > 0;
        const values = filter.values
          .split(',')
          .map((value) => value.trim())
          .filter((value) => value.length > 0);

        if (!hasColumn && values.length === 0) {
          return '';
        }
        if (hasColumn && values.length === 0) {
          return 'Provide at least one value for this column.';
        }
        if (!hasColumn && values.length > 0) {
          return 'Column name is required when values are supplied.';
        }
        return '';
      });

      const containerErrors = containers.value.map((container) => {
        const name = container.name.trim();
        const width = container.width;
        const height = container.height;
        const depth = container.depth;
        const populated = name || width !== null || height !== null || depth !== null;
        if (!populated) {
          return '';
        }

        const parts: string[] = [];

        if (!name) {
          parts.push('name');
        }

        const validateNumber = (value: number | null, label: string) => {
          if (value === null || Number.isNaN(value as number)) {
            parts.push(`${label} missing`);
            return;
          }
          if (value < 0) {
            parts.push(`${label} must be non-negative`);
          }
        };

        validateNumber(width, 'width');
        validateNumber(height, 'height');
        validateNumber(depth, 'depth');

        if (parts.length === 0) {
          return '';
        }
        return parts.join(', ');
      });

      let datesError = '';
      const hasStart = !!form.analysisStartDate;
      const hasEnd = !!form.analysisEndDate;
      if (hasStart !== hasEnd) {
        datesError = 'Both start and end dates are required when filtering by date.';
      } else if (hasStart && hasEnd && form.analysisEndDate < form.analysisStartDate) {
        datesError = 'End date must fall after the start date.';
      }

      let dataStreamError = '';
      if (props.dataStreamID && props.dataStreamID <= 0) {
        dataStreamError = 'DataStreamId must be a positive integer.';
      }

      return {
        orderDataTableName: form.orderDataTableName ? '' : 'Order data table is required.',
        skuDataTableName: form.skuDataTableName ? '' : 'SKU data table is required.',
        dates: datesError,
        filters: filterErrors,
        containers: containerErrors,
        dataStream: dataStreamError
      };
    });

    const isFormValid = computed(() => {
      const value = errors.value;
      const baseValid = !value.orderDataTableName && !value.skuDataTableName && !value.dates && !value.dataStream;
      const filtersValid = value.filters.every((entry) => !entry);
      const containersValid = value.containers.every((entry) => !entry);
      return baseValid && filtersValid && containersValid;
    });

    const showErrors = computed(() => submitAttempted.value || Object.keys(touchedFields).length > 0);

    const filtersPayload = computed(() => buildFiltersPayload());
    const containersPayload = computed(() => buildContainersPayload());

    const requestPayload = computed(() => {
      const payload: Record<string, unknown> = {
        OrderDataTableName: form.orderDataTableName,
        SKUDataTableName: form.skuDataTableName
      };

      if (form.analysisStartDate && form.analysisEndDate) {
        payload.AnalysisStartDate = form.analysisStartDate;
        payload.AnalysisEndDate = form.analysisEndDate;
      }

      const filtersValue = filtersPayload.value;
      if (Object.keys(filtersValue).length > 0) {
        payload.Filters = filtersValue;
      }

      if (form.ensureIndices !== true) {
        payload.EnsureIndices = form.ensureIndices;
      }

      if (form.workingTableOverride.trim().length > 0) {
        payload.WorkingTableOverride = form.workingTableOverride.trim();
      }

      const containersValue = containersPayload.value;
      if (containersValue.length > 0) {
        payload.OutboundOrderContainersJson = JSON.stringify(containersValue, null, 2);
      }

      if (props.dataStreamID && props.dataStreamID > 0) {
        payload.DataStreamId = props.dataStreamID;
      }

      return payload;
    });

    const requestJsonPreview = computed(() => JSON.stringify(requestPayload.value, null, 2));

    const containersJsonPreview = computed(() => {
      const payload = containersPayload.value;
      if (payload.length === 0) {
        return '// No outbound order containers will be sent.';
      }
      return JSON.stringify(payload, null, 2);
    });

    const windowLabel = computed(() => {
      const start = result.value?.summary?.window?.start;
      const end = result.value?.summary?.window?.end;
      if (!start && !end) {
        return 'Not specified';
      }
      if (start && end) {
        return `${start} ? ${end}`;
      }
      return start || end || 'Not specified';
    });

    const headline = computed(() => result.value?.summary?.headline ?? {});

    const formatCompactJson = (data: unknown) => {
      if (!data || (typeof data === 'object' && Object.keys(data as Record<string, unknown>).length === 0)) {
        return '// Empty';
      }
      try {
        return JSON.stringify(data, null, 2);
      } catch (error) {
        return String(data);
      }
    };

    const extractErrorMessage = (error: unknown) => {
      const axiosError = error as AxiosError<{ message?: string; error?: string; title?: string }>;
      const responseMessage = axiosError?.response?.data?.message || axiosError?.response?.data?.error || axiosError?.response?.data?.title;
      if (responseMessage) {
        return responseMessage;
      }
      if (axiosError?.message) {
        return axiosError.message;
      }
      if (error instanceof Error) {
        return error.message;
      }
      return 'Unable to run Auto System Classification.';
    };

    const registerMaterialisedTables = async (tables: string[]) => {
      if (!Array.isArray(tables) || tables.length === 0 || !props.dataStreamID) {
        return;
      }

      const description = 'Auto System Classification result';

      for (const tableName of tables) {
        if (typeof tableName !== 'string' || tableName.trim().length === 0) {
          continue;
        }
        try {
          await axios.post(`${BASE_HSUITE_URL}/api/DataTable/CreateDataTable`, {
            DataStreamID: props.dataStreamID,
            TableID: tableName,
            Name: tableName,
            Description: description
          });
        } catch (error) {
          console.warn(`Failed to register table ${tableName}:`, error);
        }
      }
    };

    const submitForm = async () => {
      submitAttempted.value = true;
      markTouched('orderDataTableName');
      markTouched('skuDataTableName');
      if (!isFormValid.value) {
        return;
      }

      isSubmitting.value = true;
      apiError.value = null;

      try {
        const response = await axios.post(`${BASE_HSUITE_URL}/api/AutoSystemClassification/RunAutoSystemClassification`, requestPayload.value);
        result.value = response.data;
        materialisedTables.value = Array.isArray(response.data?.materialised) ? response.data.materialised : [];
        await registerMaterialisedTables(materialisedTables.value);
      } catch (error) {
        console.error('Failed to run Auto System Classification', error);
        apiError.value = extractErrorMessage(error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const resetForm = () => {
      form.orderDataTableName = '';
      form.skuDataTableName = '';
      form.analysisStartDate = '';
      form.analysisEndDate = '';
      form.ensureIndices = true;
      form.workingTableOverride = '';
      filters.value = [];
      containers.value = [];
      orderTableSearch.value = '';
      skuTableSearch.value = '';
      advancedOpen.value = false;
      submitAttempted.value = false;
      apiError.value = null;
      result.value = null;
      materialisedTables.value = [];
      Object.keys(touchedFields).forEach((key) => {
        delete touchedFields[key];
      });
    };

    watch(
      () => props.dataStreamID,
      (newValue) => {
        if (newValue) {
          fetchOrderTables();
          fetchSkuTables();
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      if (props.dataStreamID) {
        fetchOrderTables();
        fetchSkuTables();
      }
    });

    return {
      form,
      filters,
      containers,
      orderTables,
      skuTables,
      orderTableSearch,
      skuTableSearch,
      filteredOrderTables,
      filteredSkuTables,
      addFilter,
      removeFilter,
      addContainer,
      removeContainer,
      markTouched,
      isSubmitting,
      submitForm,
      resetForm,
      errors,
      isFormValid,
      showErrors,
      requestJsonPreview,
      containersJsonPreview,
      advancedOpen,
      apiError,
      result,
      windowLabel,
      headline,
      formatCompactJson,
      materialisedTables
    };
  }
});
</script>

<style scoped>
.preview-block {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 12px;
  max-height: 280px;
  overflow: auto;
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  white-space: pre-wrap;
}

.advanced-wrapper button {
  min-width: 220px;
}

.alert {
  font-size: 14px;
}
</style>





